<script setup lang="ts">
import BlogBreadcrumb from '@/components/blog/BlogBreadcrumb.vue'
import BlogCard from '@/components/blog/BlogCard.vue'
import BlogFAQ from '@/components/blog/BlogFaq.vue'
import type { Blog } from '@/data/blogs'
import { formatDateToISO, getBlogBySlug, getBlogFolderByCategory } from '@/data/blogs'
import { blogs } from '@/data/blogs'
import { getLatestBlogs } from '@/data/blogs'
import { easeOut, motion } from 'motion-v'

const route = useRoute()

// Get the slug from the URL (e.g., "why-restaurant-marketing-feels-complicated")
const slugFromUrl = route.path.split('/').pop()

const blog = getBlogBySlug(slugFromUrl?.toString() || '')

const lastThreeBlogs = getLatestBlogs(
  blogs.filter(blogItem => blogItem.slug === 'why-restaurant-websites-dont-convert' || blogItem.slug === 'ai-in-restaurant-marketing' || blogItem.slug === 'email-marketing-for-restaurants'),
  3,
)

const headlineVariants = {
  initial: { opacity: 0, y: '10%' },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: easeOut } },
}

const paragraphVariants = {
  initial: { opacity: 0, y: 20 },
  animate: (custom: unknown) => {
    const i = Number(custom)
    return {
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.05, duration: 0.4, ease: easeOut },
    }
  },
}

const blogVariants = {
  initial: { opacity: 0, y: 50 },
  animate: (custom: unknown) => {
    const i = Number(custom)
    return {
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.5, ease: easeOut },
    }
  },
}

const blogSchema = {
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  'headline': blog?.seo?.title,
  'image': [
    blog?.image,
  ],
  'datePublished': formatDateToISO(blog?.seo?.datePublished || ''),
  // 'dateModified': blog?.seo?.dateModified,
  // 'author': blog?.seo?.author?.map(a => ({
  //   '@type': a['@type'],
  //   'name': a.name,
  //   'url': a.url,
  // })) || [],
}

const faqSchema = computed(() => {
  if (!blog?.faq?.length) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': blog.faq.map(item => ({
      '@type': 'Question',
      'name': item.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': item.answer,
      },
    })),
  }
})

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  'itemListElement': [
    {
      '@type': 'ListItem',
      'position': 1,
      'name': 'Home',
      'item': `${import.meta.env.VITE_MONOLITH_URL}/`,
    },
    {
      '@type': 'ListItem',
      'position': 2,
      'name': 'Blog',
      'item': `${import.meta.env.VITE_MONOLITH_URL}/blog`,
    },
    {
      '@type': 'ListItem',
      'position': 3,
      'name': blog?.category,
      'item': `${import.meta.env.VITE_MONOLITH_URL}/blog?category=${encodeURIComponent(blog?.category || '')}`,
    },
    {
      '@type': 'ListItem',
      'position': 4,
      'name': blog?.title,
      'item': `${import.meta.env.VITE_MONOLITH_URL}/blog/${getBlogFolderByCategory(blog?.category || '')}/${slugFromUrl}`,
    },
  ],
}

useHead({
  link: [{
    rel: 'canonical',
    href: `${import.meta.env.VITE_MONOLITH_URL}/blog/${getBlogFolderByCategory(blog?.category || '')}/${slugFromUrl}`,
  }],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(blogSchema), // BlogPosting
    },
    blog?.faq?.length
      ? {
          type: 'application/ld+json',
          innerHTML: JSON.stringify(faqSchema.value), // FAQPage
        }
      : null,
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(breadcrumbSchema),
    },
  ].filter(Boolean),
})

useSeoMeta({
  title: blog?.seo?.title,
  description: blog?.seo?.description,
  keywords: blog?.seo?.keywords.join(', '),
  ogTitle: blog?.seo?.title,
  ogDescription: blog?.seo?.description,
  ogImage: blog?.seo?.ogImage,
})
</script>
<template>
  <div class="isolate">
    <LayoutNavbar />

    <div
      class="mb-0 bg-gradient-to-b from-[#f8f8f0] from-50% to-[#f8f8f0] pt-4  md:shadow-2xl md:shadow-black/10"
    >
      <section class="mx-auto mb-8 max-w-screen-xl px-4 md:px-8 lg:max-w-screen-xl xl:px-8">
        <BlogBreadcrumb
          :blog="blog as Blog"
        />
        <motion.h1
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-3xl font-medium text-gray-900 lg:text-4xl"
        >
          {{ blog?.title }}
        </motion.h1>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-slate-700"
        >
          <router-link
            :to="{ path: '/blog', query: { category: blog?.category } }"
            class="text-[#FF7118] underline"
          >
            {{ blog?.category }}
          </router-link> • {{ blog?.date }}
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          Key Takeaways (30 Seconds)
        </motion.h2>

        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-4 text-left text-slate-700"
        >
          Restaurants that appear at the top of Google capture the majority of diners at the moment of decision, making search visibility—driven by SEO, reviews, and website quality—a direct driver of reservations, traffic, and revenue.
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>Restaurants that rank higher on Google capture more guests, especially during peak decision moments
            </span>
          </li>
          <li>
            <span>
              If your restaurant doesn’t appear in the top 5-10 Google results, most diners will likely never see it
            </span>
          </li>
          <li>
            <span>
              Google visibility directly impacts reservations, orders, and walk-in traffic

            </span>
          </li>
          <li>
            <span>
              Local search engine optimization (SEO) factors like reviews, website quality, and Google Business Profile determine rankings

            </span>
          </li>
          <li>
            <span>
              Small, consistent updates can significantly improve your restaurant’s search visibility

            </span>
          </li>
        </motion.ul>
        <div class="mb-5 mt-8 flex flex-col items-stretch gap-4 md:flex-row">
          <motion.img
            :initial="{ opacity: 0, y: 50 }"
            :while-in-view="{ opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOut } }"
            :src="blog?.image"
            :alt="blog?.alt"
            class=" w-full rounded-2xl object-cover drop-shadow-lg md:w-7/12"
          />

          <div class="w-full rounded-2xl border-4 border-[#FF711833] bg-white px-4 py-2 md:w-5/12">
            <motion.p
              :variants="paragraphVariants"
              :custom="0"
              initial="initial"
              while-in-view="animate"
              class="relative w-full text-xl leading-relaxed text-slate-700"
            >
              <svg
                width="35"
                height="28"
                viewBox="0 0 35 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="inline pb-1"
              >
                <path
                  d="M33.7768 1.80041e-06C34.496 1.86329e-06 35 0.546003 35 1.1585C35 1.59425 34.7463 2.0615 34.1303 2.38875C31.0013 4.046 27.3788 9.78425 27.3788 13.3228C32.6568 13.1618 34.9493 17.8413 34.9493 20.7743C34.9493 24.2848 31.9077 28 27.4102 28C21.7385 28 18.9577 23.5427 18.9577 18.7127C18.9578 8.246 30.9173 1.55042e-06 33.7768 1.80041e-06ZM14.819 1.43069e-07C15.5365 2.05795e-07 16.0423 0.546002 16.0423 1.1585C16.0423 1.59425 15.7885 2.0615 15.1725 2.38875C12.0435 4.046 8.421 9.78425 8.421 13.3227C13.6972 13.1617 15.9915 17.8412 15.9915 20.7742C15.9915 24.2847 12.9482 28 8.4525 28C2.78075 28 -3.42503e-06 23.5427 -3.00278e-06 18.7127C-2.08775e-06 8.246 11.9578 -1.07069e-07 14.819 1.43069e-07Z"
                  fill="#FF7118"
                />
              </svg>
              <span class="">
                Restaurants that rank well benefit from a compounding effect: Visibility → traffic → reviews → trust → stronger rankings.</span>
              <svg
                width="35"
                height="28"
                viewBox="0 0 35 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="inline pt-1"
              >
                <path
                  d="M1.22325 28C0.504 28 0 27.454 0 26.8415C0 26.4057 0.25375 25.9385 0.86975 25.6112C3.99875 23.954 7.62125 18.2157 7.62125 14.6772C2.34325 14.8382 0.0507501 10.1587 0.0507501 7.22575C0.0507501 3.71525 3.09225 0 7.58975 0C13.2615 0 16.0423 4.45725 16.0423 9.28725C16.0423 19.754 4.08275 28 1.22325 28ZM20.181 28C19.4635 28 18.9578 27.454 18.9578 26.8415C18.9578 26.4057 19.2115 25.9385 19.8275 25.6112C22.9565 23.954 26.579 18.2157 26.579 14.6772C21.3028 14.8382 19.0085 10.1587 19.0085 7.22575C19.0085 3.71525 22.0518 0 26.5475 0C32.2193 0 35 4.45725 35 9.28725C35 19.754 23.0423 28 20.181 28Z"
                  fill="#FF7118"
                />
              </svg>
            </motion.p>
          </div>
        </div>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 pt-6 text-slate-700"
        >
          When diners search for places to eat, decisions happen instantly on Google Search and Maps. If your restaurant isn’t showing up, it’s not just a missed click—it’s lost guests, reservations, and revenue to competitors who are easier to find.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          Why Doesn’t My Restaurant Show Up on Google?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          If your restaurant isn’t showing up, Google doesn’t yet have enough confidence to rank it highly.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Google prioritizes businesses based on:
        </motion.p>

        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>
              Relevance (how well you match the search)

            </span>
          </li>
          <li>
            <span>
              Distance (how close you are to the diner)

            </span>
          </li>
          <li>
            <span>
              Prominence (how trusted and active you appear online)

            </span>
          </li>
        </motion.ul>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          How Do Diners Find Restaurants on Google?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          If you are not showing up on Google, it’s helpful to first understand what’s actually causing the problem. Most local searches lead to a visit within 24 hours, and almost no one clicks past page one. If you’re not visible at the moment of search, you’re not part of the decision.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Today’s diners search with high intent—they’re ready to choose. Common searches include:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li><span>“restaurants near me open now”</span></li>
          <li>
            <span>
              “best Italian restaurant near me”

            </span>
          </li>
          <li>
            <span>
              “best tacos in [city]”

            </span>
          </li>
          <li>
            <span>
              “restaurants with outdoor seating”

            </span>
          </li>
          <li>
            <span>
              “[restaurant name] reservations”

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          These searches trigger Google’s ranking system across Search and Maps. If your restaurant appears near the top, you get the click. If not, your competitor does.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          What Are Local Search Rankings for Restaurants?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Local search rankings determine where your restaurant appears in Google Search and Google Maps. Google evaluates your overall online presence—your website, reviews, listings, and activity—to decide who shows up first.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          The most valuable placement is the Google Map Pack (top 3 results). These listings capture the majority of clicks, calls, directions, and reservations. If you’re not in the top 3, your visibility and opportunity drop significantly.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          What Happens If Your Restaurant Doesn’t Rank Well?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Poor rankings don’t always feel obvious, but they quietly impact your business.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          You may notice slower nights, fewer new guests, or increased reliance on third-party apps.  Meanwhile, higher-ranking competitors get discovered first, collect more reviews, and build momentum over time.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          That gap compounds. The longer your restaurant stays less visible, the harder it becomes to catch up.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          How Can Restaurants Improve Local Search Rankings?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          You don’t need a large budget, just consistent execution.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Restaurants improve rankings by strengthening the signals Google uses to evaluate trust and relevance.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          The most impactful actions include:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-slate-700"
        >
          <li>
            <span>Keeping your Google Business Profile complete and active, including syncing keywords, attributes, professional photos, and video to the profile

            </span>
          </li>
          <li>
            <span>Generating a steady flow of recent reviews and AI-suggested responses to reviews

            </span>
          </li>
          <li>
            <span>Using a website optimized for local search

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          These signals help Google understand your restaurant is active, reliable, and worth showing to nearby diners.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          How Can Restaurant Marketing Tools Improve Google Rankings?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Every day, diners search for places to eat nearby—and marketing tools help ensure your restaurant shows up when they do.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Platforms like SpotHopper help restaurants maintain consistent visibility across Google Search, Maps, and online listings by keeping websites optimized, reviews growing, and business information accurate.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Restaurants using connected systems often see increased website traffic, stronger rankings, and more direct reservations—without spending hours managing it manually.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl"
        >
          Fill Your Tables with Better Local Search Visibility
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Every search is a decision moment. The question is simple: will diners find your restaurant—or someone else’s?
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-slate-700"
        >
          Learn more about how SpotHopper helps restaurants improve visibility across Google Search, Maps, and online listings—and turn more searches into guests.
        </motion.p>
      </section>

      <div class="mb-8 w-full bg-white  pb-8 pt-8 md:rounded-[5rem] ">
        <BlogFAQ :items="blog?.faq" />
        <section
          v-if="lastThreeBlogs.length"
          class="mx-auto mb-8 px-2 lg:max-w-screen-xl 2xl:px-0"
        >
          <motion.h2
            :variants="headlineVariants"
            initial="initial"
            while-in-view="animate"
            class="px-4 pt-6 text-left text-2xl font-medium text-gray-900 lg:text-3xl xl:px-0"
          >
            Keep learning about <span class="lowercase">{{ blog.category }}</span>
          </motion.h2>
          <div class="grid w-full grid-cols-1 gap-x-6 gap-y-12 px-4 py-6 md:grid-cols-3 xl:px-0">
            <motion.div
              v-for="(item, index) in lastThreeBlogs"
              :key="item.slug"
              :custom="index"
              :variants="blogVariants"
              initial="initial"
              while-in-view="animate"
            >
              <BlogCard v-bind="item" />
            </motion.div>
          </div>
        </section>
        <section class="mx-auto px-2 lg:max-w-screen-xl lg:pb-16 2xl:px-0">
          <div class="relative overflow-clip rounded-3xl px-4 py-10 md:px-8 md:py-20">
            <div class="absolute inset-0 isolate before:absolute before:inset-0 before:z-[1] before:bg-gray-800/70">
              <motion.img
                :initial="{ opacity: 0, y: 50 }"
                :while-in-view="{ opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOut } }"
                :src="`${S3_ASSETS_URL}/home/cta-02.jpg`"
                class="aspect-video size-full object-cover object-center"
                role="presentation"
              />
            </div>

            <div class="isolate grid place-items-center gap-5">
              <motion.h3
                :variants="headlineVariants"
                initial="initial"
                while-in-view="animate"
                class="max-w-screen-sm text-balance text-center text-3xl leading-tight text-white lg:text-5xl"
              >
                Ready to take your restaurant marketing to the next level?
              </motion.h3>

              <UiButton
                class="group w-full max-w-xs lg:max-w-max"
                as-child
              >
                <RouterLink :to="{ name: '/free-demo' }">
                  Book a Free Demo

                  <UiIcon
                    name="arrow-forward-rounded"
                    class="transition-transform duration-200 group-hover:translate-x-1"
                  />
                </RouterLink>
              </UiButton>
            </div>
          </div>
        </section>
      </div>

      <LayoutFooter />
    </div>
  </div>
</template>
